"use client";

import { useMutation } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { datadogRum } from "@datadog/browser-rum";
import { postApiFeedbackPracticeId } from "@/lib/api-client/endpoints/feedback/feedback";
import { useTenantRouteContext } from "@/components/app-shell/TenantRouteContext";
import { isDatadogRumConfigured } from "@/lib/observability/datadog-rum";
import { LOCATION_OPTIONS } from "../components/FeedbackLocationField";
import type { FeedbackFormValues } from "./useFeedbackForm";

interface Props {
  onSuccess?: VoidFunction;
}

function getDatadogContext() {
  if (!isDatadogRumConfigured()) return {};

  const context = datadogRum.getInternalContext();

  return {
    datadog_session_id: context?.session_id,
    datadog_application_id: context?.application_id,
    datadog_view: context?.view?.id,
    session_replay_url: datadogRum.getSessionReplayLink(),
  };
}

const useSubmitFeedback = ({ onSuccess }: Props = {}) => {
  const { tenantId } = useTenantRouteContext();
  const pathname = usePathname();

  const mutation = useMutation({
    mutationFn: (values: FeedbackFormValues) =>
      postApiFeedbackPracticeId(tenantId, {
        feedback_type: values.issueType,
        where: LOCATION_OPTIONS.find(
          (option) => option.value === values.location,
        )?.label,
        description: values.description,
        severity: values.severity,
        screenshot: values.screenShot ?? undefined,
        app_version: process.env.NEXT_PUBLIC_RELEASE_VERSION,
        user_agent: navigator.userAgent,
        route: pathname,
        timestamp: new Date().toISOString(),
        ...getDatadogContext(),
      }),
    onSuccess,
  });

  const handleSubmit = (values: FeedbackFormValues) => {
    mutation.mutate(values);
  };

  return { handleSubmit, isLoading: mutation.isPending };
};

export default useSubmitFeedback;
